import { useAppSSE } from 'contexts/AppSSEContext';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  applyIpawsMessageUpdate,
  IPAWS_MESSAGE_UPDATE_EVENT,
} from 'services/ipaws/ipaws.event.service';

// Same dev-only helper the other SSE hooks use, resolved once at module load.
const notifySseActivity =
  process.env.NODE_ENV === 'development'
    ? require('components/DevRenderOverlay/sseActivityHelper').notifySseActivity
    : () => {};

type Subscribe = (eventType: string, handler: (eventData: unknown) => void) => () => void;

/**
 * FEMA IPAWS events (FE-03, decided 2026-08-26). `ipaws-message-update` tells
 * every operator of the workplace that a message's status changed: the
 * submitting screen releases its hold (the HTTP answer or this event, whichever
 * lands first) and everyone refreshes the FEMA alert list.
 */
export default function useIpawsEvents({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const { subscribe } = useAppSSE() as { subscribe: Subscribe };

  const handlers = useMemo(
    () => ({
      [IPAWS_MESSAGE_UPDATE_EVENT]: (eventData: unknown) => {
        notifySseActivity(IPAWS_MESSAGE_UPDATE_EVENT);
        applyIpawsMessageUpdate(dispatch, eventData);
      },
    }),
    [dispatch],
  );

  useEffect(() => {
    if (!enabled) return;

    const unsubs = Object.entries(handlers).map(([eventType, handler]) =>
      subscribe(eventType, handler),
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [enabled, subscribe, handlers]);
}
